import React, { useCallback } from 'react';
import { Box } from '@mui/material';
import SplitPane, { Pane } from 'split-pane-react';
import 'split-pane-react/esm/themes/default.css';
import '../../css/splitPaneDivider.css';
import { Topbar } from '../Topbar.js';
import MeetingsList from '../MeetingsList';
import MeetingSearchBar from '../MeetingSearchBar';
import SortingControls from '../SortingControls';
import LoadingIndicator from '../LoadingIndicator';
import ConfirmationDialog from '../ConfirmationDialog';
import ShareDialog from '../ShareDialog';
import { getSentence } from '../translations.js';
import { useMeetingContext } from '../../context/MeetingContext';

const DesktopLayout = ({
  // Panel state
  sizes,
  updatePanelSizes,
  
  // Meeting data
  meetings,
  processedMeetings,
  selectedMeetingId,
  sortingLoading,
  
  // Dialog state
  pendingDeleteId,
  pendingShareId,
  
  // Event handlers
  onMeetingSelect,
  onMeetingDelete,
  onMeetingTogglePin,
  onMeetingShare,
  onDeleteConfirm,
  onDeleteCancel,
  onShareClose,
  onFileUploadSuccess,
  loadMeetings, // Add loadMeetings for sorting changes
  
  // Accordion state
  pinnedAccordionExpanded,
  regularAccordionExpanded,
  onPinnedAccordionChange,
  onRegularAccordionChange,
  
  // Right panel content
  renderRightPanel
}) => {
  // Use meeting context for search functionality and sorting
  const {
    searchResults,
    isSearching,
    searchMeetings: handleSearch,
    clearSearch: handleClearSearch,
    getDisplayMeetings,
    sortBy,
    sortDescending,
    setSortParams
  } = useMeetingContext();

  // Handle sorting changes (client-side only, no API calls)
  const handleOrderByChange = useCallback((newOrderBy) => {
    setSortParams(newOrderBy, sortDescending);
  }, [setSortParams, sortDescending]);

  const handleDescendingChange = useCallback((newDescending) => {
    setSortParams(sortBy, newDescending);
  }, [setSortParams, sortBy]);

  // Use search results if available, otherwise use processed meetings
  const displayMeetings = getDisplayMeetings();

  // Layout CSS for consistent styling
  const layoutCSS = {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  return (
    <div style={{ height: "100vh", overflow: "clip" }}>
      <Topbar onFileUploadSuccess={onFileUploadSuccess} />
      
      {/* Dialogs */}
      <ConfirmationDialog
        open={!!pendingDeleteId}
        title={getSentence(6)}
        message={getSentence(7)}
        confirmText={getSentence(9)}
        cancelText={getSentence(8)}
        onConfirm={onDeleteConfirm}
        onCancel={onDeleteCancel}
      />

      <ShareDialog
        open={!!pendingShareId}
        meetingId={pendingShareId}
        onClose={onShareClose}
      />

      {/* Desktop Split Panel */}
      <SplitPane
        split="vertical"
        sizes={sizes}
        onChange={(newSizes) => updatePanelSizes([newSizes[0], 'auto'])}
        style={{ 
          zIndex: '3',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' // Smooth easing
        }}
      >
        <Pane>
          <div style={{ 
            ...layoutCSS, 
            background: '#ddd', 
            alignItems: "top", 
            overflow: "hidden",
            height: "100%"
          }}>
            <Box sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}>
              {/* Search Bar - Fixed at top */}
              {meetings.length > 0 && (
                <Box>
                  <MeetingSearchBar
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                    isSearching={isSearching}
                    searchResults={searchResults}
                    orderBy={sortBy}
                    descending={sortDescending}
                  />
                  {/* Sorting Controls - Always visible */}
                  <SortingControls
                    orderBy={sortBy}
                    descending={sortDescending}
                    onOrderByChange={handleOrderByChange}
                    onDescendingChange={handleDescendingChange}
                    disabled={isSearching}
                  />
                </Box>
              )}
              
              {/* Meetings List - Scrollable below search */}
              <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                {/* Unified Loading Indicator at top of meeting list */}
                <LoadingIndicator
                  isLoading={isSearching}
                  message="Searching..."
                  size="small"
                  position="inline"
                  style={{
                    justifyContent: 'center',
                    padding: '12px 0',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderBottom: '1px solid #f0f0f0',
                    backdropFilter: 'blur(4px)'
                  }}
                />
                
                {displayMeetings.length > 0 ? (
                  <MeetingsList
                    meetings={displayMeetings}
                    selectedMeetingId={selectedMeetingId}
                    pinnedAccordionExpanded={pinnedAccordionExpanded}
                    regularAccordionExpanded={regularAccordionExpanded}
                    onMeetingSelect={onMeetingSelect}
                    onMeetingDelete={onMeetingDelete}
                    onMeetingTogglePin={onMeetingTogglePin}
                    onMeetingShare={onMeetingShare}
                    onPinnedAccordionChange={onPinnedAccordionChange}
                    onRegularAccordionChange={onRegularAccordionChange}
                  />
                ) : searchResults !== null && searchResults.length === 0 && !isSearching ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '100%',
                    color: '#999',
                    fontStyle: 'italic'
                  }}>
                    No meetings found matching your search criteria.
                  </Box>
                ) : null}
              </Box>
            </Box>
          </div>
        </Pane>
        
        <div style={{ 
          ...layoutCSS, 
          background: '#EBEBEB', 
          alignItems: "top", 
          overflowY: "scroll" 
        }}>
          {renderRightPanel()}
        </div>
      </SplitPane>
    </div>
  );
};

export default DesktopLayout;